// Typed Client Book API client for the web layer.
//
// Wraps the `clientBookContract` endpoints via the shared `api` client and
// exposes react-query hooks. Mutations invalidate `clientBookKeys.all` so the
// directory and the detail view re-render together.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, staffErrorMessage } from '@/lib/api';
import type {
    AddClientToHouseholdRequest,
    AddRelatedPersonRequest,
    ClientDetail,
    ClientExternalIdentifiersResponse,
    CreateClientRequest,
    CreateHouseholdRequest,
    ListClientsResponse,
    ListHouseholdsResponse,
    ListTriTierResponse,
    MatchCandidate,
    RegisterPortfolioRequest,
    UpdateClientRequest,
} from '@septima/contract';

export interface ClientBookQuery {
    search?: string;
    sort?: 'aum' | 'name' | 'segment' | 'performance' | 'since';
    segment?: string;
    serviceType?: string;
    investmentManagerUserId?: string;
}

export const clientBookKeys = {
    all: ['client-book'] as const,
    list: (q?: ClientBookQuery) => ['client-book', 'list', q ?? {}] as const,
    households: ['client-book', 'households'] as const,
    triTier: (search?: string) => ['client-book', 'tri-tier', search ?? ''] as const,
    matches: ['client-book', 'matches'] as const,
    detail: (clientId: string) => ['client-book', 'detail', clientId] as const,
    externalIds: (clientId: string) => ['client-book', 'external-ids', clientId] as const,
};

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export function useClientBook(query?: ClientBookQuery) {
    return useQuery({
        queryKey: clientBookKeys.list(query),
        queryFn: async (): Promise<ListClientsResponse> => {
            const res = await api.clientBook.listClients({ query: query ?? {} });
            if (res.status !== 200) throw new Error(`Client Book failed: ${res.status}`);
            return res.body;
        },
        staleTime: 10_000,
    });
}

export function useClientBookHouseholds(enabled = true) {
    return useQuery({
        queryKey: clientBookKeys.households,
        enabled,
        queryFn: async (): Promise<ListHouseholdsResponse> => {
            const res = await api.clientBook.listHouseholds();
            if (res.status !== 200) throw new Error(`Households failed: ${res.status}`);
            return res.body;
        },
        staleTime: 10_000,
    });
}

// Tri-tier Client Book (ADR-0015 Stage 5) — one row per Account,
// joined up to its Suitability Record + primary Contact. A separate endpoint
// from `useClientBook` (which is person-grained); enabled on demand so the
// person-grained default mode never fetches it.
export function useClientBookTriTier(search?: string, enabled = true) {
    return useQuery({
        queryKey: clientBookKeys.triTier(search),
        enabled,
        queryFn: async (): Promise<ListTriTierResponse> => {
            const res = await api.clientBook.listTriTier({
                query: search ? { search } : {},
            });
            if (res.status !== 200) throw new Error(`Client Book (tri-tier) failed: ${res.status}`);
            return res.body;
        },
        staleTime: 10_000,
    });
}

export function useClientMatchCandidates() {
    return useQuery({
        queryKey: clientBookKeys.matches,
        queryFn: async (): Promise<MatchCandidate[]> => {
            const res = await api.clientBook.matchCandidates();
            if (res.status !== 200) throw new Error(`Match candidates failed: ${res.status}`);
            return res.body.candidates;
        },
        staleTime: 30_000,
    });
}

export function useClientDetail(clientId: string | undefined) {
    return useQuery({
        queryKey: clientBookKeys.detail(clientId ?? ''),
        enabled: Boolean(clientId),
        queryFn: async (): Promise<ClientDetail> => {
            const res = await api.clientBook.getDetail({ params: { clientId: clientId! } });
            if (res.status !== 200) throw new Error(staffErrorMessage(res.body, res.status));
            return res.body;
        },
        staleTime: 5_000,
    });
}

// The client's external IDs from the identity registry (Identity panel on the
// KYC tab). Read-only; resolves to the Septima id + the crosswalked refs.
export function useExternalIdentifiers(clientId: string | undefined) {
    return useQuery({
        queryKey: clientBookKeys.externalIds(clientId ?? ''),
        enabled: Boolean(clientId),
        queryFn: async (): Promise<ClientExternalIdentifiersResponse> => {
            const res = await api.clientBook.getExternalIds({ params: { clientId: clientId! } });
            if (res.status !== 200) throw new Error(staffErrorMessage(res.body, res.status));
            return res.body;
        },
        staleTime: 30_000,
    });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export function useCreateClient() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: CreateClientRequest): Promise<string> => {
            const res = await api.clientBook.createClient({ body: input });
            if (res.status !== 200) throw new Error(staffErrorMessage(res.body, res.status));
            return res.body.clientId;
        },
        onSuccess: () => void qc.invalidateQueries({ queryKey: clientBookKeys.all }),
    });
}

export function useRegisterPortfolio(accountId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: RegisterPortfolioRequest): Promise<string> => {
            const res = await api.clientBook.registerPortfolio({
                params: { accountId },
                body: input,
            });
            if (res.status !== 200) throw new Error(staffErrorMessage(res.body, res.status));
            return res.body.id ?? '';
        },
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: clientBookKeys.all });
        },
    });
}

export function useCreateHousehold() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: CreateHouseholdRequest): Promise<string> => {
            const res = await api.clientBook.createHousehold({ body: input });
            if (res.status !== 200) throw new Error(staffErrorMessage(res.body, res.status));
            return res.body.id ?? '';
        },
        onSuccess: () => void qc.invalidateQueries({ queryKey: clientBookKeys.all }),
    });
}

export function useAddClientToHousehold() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: {
            householdId: string;
            clientId: string;
            relationship?: AddClientToHouseholdRequest['relationship'];
            description?: string;
        }): Promise<void> => {
            const res = await api.clientBook.addClientToHousehold({
                params: { householdId: input.householdId },
                body: {
                    clientId: input.clientId,
                    relationship: input.relationship,
                    description: input.description,
                },
            });
            if (res.status !== 200) throw new Error(staffErrorMessage(res.body, res.status));
        },
        onSuccess: () => void qc.invalidateQueries({ queryKey: clientBookKeys.all }),
    });
}

export function useAddRelatedPerson(householdId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: AddRelatedPersonRequest): Promise<void> => {
            const res = await api.clientBook.addRelatedPerson({
                params: { householdId },
                body: input,
            });
            if (res.status !== 200) throw new Error(staffErrorMessage(res.body, res.status));
        },
        onSuccess: () => void qc.invalidateQueries({ queryKey: clientBookKeys.all }),
    });
}

export function useConfirmMatch() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: {
            canonicalClientId: string;
            duplicateClientId: string;
        }): Promise<void> => {
            const res = await api.clientBook.confirmMatch({ body: input });
            if (res.status !== 200) throw new Error(staffErrorMessage(res.body, res.status));
        },
        onSuccess: () => void qc.invalidateQueries({ queryKey: clientBookKeys.all }),
    });
}

export function useUpdateClient() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: {
            clientId: string;
            patch: UpdateClientRequest;
        }): Promise<string> => {
            const res = await api.clientBook.updateClient({
                params: { clientId: input.clientId },
                body: input.patch,
            });
            if (res.status !== 200) throw new Error(staffErrorMessage(res.body, res.status));
            return res.body.clientId;
        },
        onSuccess: () => void qc.invalidateQueries({ queryKey: clientBookKeys.all }),
    });
}
